import { Inject, Injectable, Injector } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isNullish } from '@y42/util/nullish';
import { BehaviorSubject, map, Observable, Subject, tap } from 'rxjs';
import { FeatureFlag, FeatureFlags, FEATURE_FLAGS } from './feature-flag.injection-token';

export interface Y42WindowFeature {
  enable: (flag: string) => void;
  disable: (flag: string) => void;
  list: () => void;
  reset: () => void;
}

interface Y42Window {
  feature: Y42WindowFeature;
}

const setFlag$$ = new Subject<{ flag: FeatureFlag; enabled: boolean }>();
const list$$ = new Subject<void>();
const reset$$ = new Subject<void>();

(window as unknown as { y42: Y42Window }).y42 = {
  ...((window as unknown as { y42: Y42Window }).y42 || {}),
  feature: {
    enable: (flag) => setFlag$$.next({ flag, enabled: true }),
    disable: (flag) => setFlag$$.next({ flag, enabled: false }),
    list: () => list$$.next(),
    reset: () => reset$$.next(),
  },
};

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class FeatureFlagService<T extends string> {
  constructor(
    @Inject(FEATURE_FLAGS) private readonly featureFlags: FeatureFlags<T>,
    private readonly injector: Injector,
  ) {
    this.initializeFlags();
    this.initializeListeners();
  }

  private readonly flags$$: BehaviorSubject<Record<T, boolean>> = new BehaviorSubject({} as Record<T, boolean>);

  select(flag: T): Observable<boolean> {
    return this.flags$$.pipe(
      map(() => this.get(flag)),
      untilDestroyed(this),
    );
  }

  get(flag: T): boolean {
    const flags = this.flags$$.value;
    const featureFlagEnabled = flags[flag];

    return featureFlagEnabled || false;
  }

  private initializeFlags() {
    const featureFlags = Object.entries(this.featureFlags)
      .map(([flag, valueOrCallback]) => ({
        flag,
        value: typeof valueOrCallback === 'function' ? valueOrCallback(this.injector) : valueOrCallback,
      }))
      .reduce((flags, { flag, value }) => {
        if (isNullish(flags[flag as T])) {
          flags[flag as T] = value;
        }
        return flags;
      }, safeJSONParse(localStorage.getItem('y42_feature_flags')) as Record<T, boolean>);
    this.flags$$.next(featureFlags);
  }

  private initializeListeners() {
    setFlag$$
      .pipe(
        tap(({ flag, enabled }) => this.set(flag as T, enabled)),
        untilDestroyed(this),
      )
      .subscribe();

    list$$
      .pipe(
        tap(() =>
          Object.keys(this.featureFlags)
            .map((flag) => ({ flag, value: this.get(flag as T) }))
            .forEach(({ flag, value }) =>
              this.print(`🏁 Flag "%c${flag}" %cis ${value ? '%cenabled ✅' : '%cdisabled 🚫'}`, value),
            ),
        ),
        untilDestroyed(this),
      )
      .subscribe();

    reset$$
      .pipe(
        tap(() => this.reset()),
        tap(() => this.print(`🏁 Flags have been %creset%c%c`)),
        tap(() => list$$.next()),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private set(flag: T, enabled: boolean) {
    const flags = this.flags$$.value;
    flags[flag] = enabled;
    this.flags$$.next(flags);
    this.persist(flag, enabled);

    this.print(`🏁 Flag "%c${flag}" %cis ${enabled ? '%cenabled ✅' : '%cdisabled 🚫'}`, enabled);
  }

  private persist(flag: T, enabled: boolean) {
    const existingPersistedFlags = safeJSONParse(localStorage.getItem(`y42_feature_flags`)) || {};
    existingPersistedFlags[flag] = enabled;

    localStorage.setItem(`y42_feature_flags`, JSON.stringify(existingPersistedFlags));
  }

  private reset() {
    localStorage.removeItem(`y42_feature_flags`);
    this.initializeFlags();
  }

  private print(log: string, value?: boolean) {
    console.log(log, 'font-weight: bold', '', `font-weight: bold; color: ${value ? 'green' : 'red'}`);
  }
}

function safeJSONParse(str?: string | null): Record<string, unknown> | null {
  try {
    return JSON.parse(str || '{}');
  } catch (err) {
    return null;
  }
}
